/**
 * Czech Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const cs: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Přehled',
  'nav.funds': 'Fondy',
  'nav.investments': 'Investice',
  'nav.investees': 'Příjemci investic',
  'nav.esg': 'ESG Přehled',
  'nav.compliance': 'Soulad',
  'nav.reports': 'Zprávy',
  'nav.settings': 'Nastavení',
  'nav.users': 'Uživatelé',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Ukazatele',
  'nav.taxonomy': 'Taxonomie EU',
  'nav.filings': 'Podání',
  'nav.calendar': 'Kalendář',
  'nav.submit': 'Odeslat data',
  'nav.submissions': 'Moje podání',
  'nav.benchmarks': 'Referenční hodnoty',
  'nav.messages': 'Zprávy',
  'nav.portfolio': 'Portfolio',
  'nav.performance': 'Výkonnost fondů',
  'nav.esgSummary': 'ESG Souhrn',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Hloubková kontrola',
  'nav.impact': 'Ukazatele dopadu',
  'nav.sdg': 'Mapování SDGs',
  'nav.audit': 'Auditní stopa',
  'nav.snapshots': 'Datové snímky',
  'nav.knowledgeGraph': 'Graf znalostí',
  'nav.geographic': 'Geografický',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Asistent',
  'nav.tasks': 'Úkoly',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorní monitor',

  // Common Actions
  'action.create': 'Vytvořit',
  'action.edit': 'Upravit',
  'action.delete': 'Smazat',
  'action.save': 'Uložit',
  'action.cancel': 'Zrušit',
  'action.submit': 'Odeslat',
  'action.approve': 'Schválit',
  'action.reject': 'Zamítnout',
  'action.download': 'Stáhnout',
  'action.export': 'Exportovat',
  'action.import': 'Importovat',
  'action.filter': 'Filtrovat',
  'action.search': 'Hledat',
  'action.refresh': 'Obnovit',
  'action.close': 'Zavřít',
  'action.back': 'Zpět',
  'action.next': 'Další',
  'action.previous': 'Předchozí',
  'action.viewAll': 'Zobrazit vše',
  'action.viewDetails': 'Zobrazit detaily',
  'action.generate': 'Generovat',
  'action.signOut': 'Odhlásit se',
  'action.addNew': 'Přidat nové',
  'action.invite': 'Pozvat',

  // Statuses
  'status.draft': 'Koncept',
  'status.submitted': 'Odesláno',
  'status.validated': 'Validováno',
  'status.approved': 'Schváleno',
  'status.rejected': 'Zamítnuto',
  'status.pending': 'Čekající',
  'status.inProgress': 'Probíhá',
  'status.overdue': 'Po termínu',
  'status.compliant': 'V souladu',
  'status.filed': 'Podáno',
  'status.active': 'Aktivní',
  'status.inactive': 'Neaktivní',
  'status.closed': 'Uzavřeno',
  'status.fundraising': 'Získávání prostředků',
  'status.investing': 'Investování',
  'status.harvesting': 'Sklizeň',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Přehled portfolia',
  'dashboard.complianceStatus': 'Stav souladu',
  'dashboard.collectionProgress': 'Průběh sběru',
  'dashboard.activityFeed': 'Aktivita',
  'dashboard.quickActions': 'Rychlé akce',
  'dashboard.investmentPipeline': 'Investiční pipeline',
  'dashboard.totalFunds': 'Celkem fondů',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktivní investice',
  'dashboard.totalInvestees': 'Příjemci investic',
  'dashboard.recentActivity': 'Nedávná aktivita',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Celkem závazků',
  'lp.capitalCalled': 'Vyžádaný kapitál',
  'lp.distributed': 'Distribuováno',
  'lp.netPosition': 'Čistá pozice',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Výkonnost fondů',
  'lp.esgSummary': 'ESG Souhrn',
  'lp.reportLibrary': 'Knihovna zpráv',
  'lp.taxonomyAligned': 'Soulad s taxonomií',
  'lp.paiCoverage': 'PAI Pokrytí',
  'lp.portfolioPerformance': 'Výkonnost LP portfolia',

  // Fund Labels
  'fund.name': 'Název fondu',
  'fund.code': 'Kód fondu',
  'fund.vintage': 'Rok vzniku',
  'fund.size': 'Velikost fondu',
  'fund.targetSize': 'Cílová velikost',
  'fund.currency': 'Měna',
  'fund.strategy': 'Strategie',
  'fund.status': 'Stav',
  'fund.sfdrCategory': 'SFDR Kategorie',
  'fund.geographyFocus': 'Geografické zaměření',
  'fund.sectorFocus': 'Sektorové zaměření',

  // Form Labels
  'form.name': 'Název',
  'form.email': 'E-mail',
  'form.country': 'Země',
  'form.amount': 'Částka',
  'form.currency': 'Měna',
  'form.date': 'Datum',
  'form.description': 'Popis',
  'form.notes': 'Poznámky',
  'form.type': 'Typ',
  'form.period': 'Období',
  'form.year': 'Rok',
  'form.required': 'Povinné',
  'form.optional': 'Nepovinné',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Závazek',
  'table.called': 'Vyžádáno',
  'table.distributed': 'Distribuováno',
  'table.tvpi': 'TVPI',
  'table.status': 'Stav',
  'table.regulation': 'Regulace',
  'table.filingType': 'Typ podání',
  'table.reportingPeriod': 'Období',
  'table.createdAt': 'Vytvořeno',
  'table.actions': 'Akce',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomie EU',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Úplnost dat',
  'compliance.nextDeadline': 'Příští termín',

  // Reports
  'report.quarterly': 'Čtvrtletní zpráva',
  'report.annual': 'Výroční zpráva',
  'report.paiStatement': 'PAI Prohlášení',
  'report.taxonomyReport': 'Zpráva o taxonomii',
  'report.generatedAt': 'Vygenerováno',
  'report.version': 'Verze',

  // General
  'general.loading': 'Načítání...',
  'general.noData': 'Žádná dostupná data',
  'general.error': 'Došlo k chybě',
  'general.retry': 'Zkusit znovu',
  'general.total': 'Celkem',
  'general.page': 'Stránka',
  'general.of': 'z',
  'general.showing': 'Zobrazeno',
  'general.results': 'výsledků',
  'general.language': 'Jazyk',
  'general.english': 'Angličtina',
  'general.french': 'Francouzština',
};
